import { ReactNode } from "react";
import { Box, Card, Skeleton, Typography } from "@mui/material";

export type DashboardTileProps = {
  title: string;
  value?: string | number;
  icon?: ReactNode;
  isLoading?: boolean;
  growth?: string;
  subtitle?: string;
  valueColor?: string;
};

export const DashboardTile = ({
  title,
  value,
  icon,
  isLoading = false,
  subtitle,
  valueColor,
  growth,
}: DashboardTileProps) => {
  const growthValue = growth ? parseFloat(growth) : 0;
  const isPositiveGrowth = growthValue >= 0;

  return (
    <Card
      sx={{
        minHeight: { xs: 148, sm: 160 },
        height: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.25 },
        borderRadius: "16px",
        border: "1px solid #E8EDF5",
        boxShadow: "0px 1px 6px rgba(15,23,42,0.04)",
        "&:hover": {
          boxShadow: "0px 4px 14px rgba(15,23,42,0.08)",
        },
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: "#94A3B8",
              letterSpacing: 1,
              textTransform: "uppercase",
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>

          {growth && (
            <Box
              sx={{
                px: 1.2,
                py: 0.25,
                borderRadius: "999px",
                backgroundColor: isPositiveGrowth ? "#ECFDF5" : "#FEF2F2",
                color: isPositiveGrowth ? "#10B981" : "#EF4444",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.2,
                flexShrink: 0,
              }}
            >
              {isPositiveGrowth ? "↗" : "↘"} {growth}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            gap: 1.5,
          }}
        >
          {isLoading ? (
            <Skeleton width={120} height={50} />
          ) : (
            <Typography
              sx={{
                fontSize: { xs: 24, sm: 28 },
                fontWeight: 700,
                color: valueColor || "#0F172A",
                lineHeight: 1,
                overflowWrap: "anywhere",
                minWidth: 0,
              }}
            >
              {value ?? "--"}
            </Typography>
          )}

          {icon && (
            <Box
              sx={{
                width: { xs: 42, sm: 48 },
                height: { xs: 42, sm: 48 },
                flex: { xs: "0 0 42px", sm: "0 0 48px" },
                borderRadius: "14px",
                backgroundColor: "#EFF6FF",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#3E8BD2",
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        {subtitle && (
          <Typography
            sx={{
              fontSize: 13,
              color: "#94A3B8",
              overflowWrap: "anywhere",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Card>
  );
};
